/**
 * useMessagesSync Hook (Story 2.3.6.5, MSG-INBOX-TABS.6 AC-5)
 *
 * Global WebSocket subscription for messaging events, at app level so the header indicator
 * updates on ALL pages, not just MessagesPage.
 *
 * Responsibilities:
 * - Subscribe to `user.{guid}.messages` and to `merchant.{g}.messages` of every shop the user
 *   is a member of (from `unread-counts.merchants`), unsubscribing when a membership goes away
 * - On MESSAGE_RECEIVED: invalidate `unread-counts` and the lists of the event's context and
 *   type; show a notification + sound when the user is NOT viewing that conversation
 * - Coordinate with ChatArea via activeConversationRef to avoid duplicates
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
  EventType,
  RealtimeTopics,
  type MessageReceivedPayload,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';
import { conversationKeys, useUnreadCounts } from '@/hooks/useConversations';
import { PERSONAL_CONTEXT, merchantContext } from '@/lib/messenger/inboxContext';
import type { UnreadCounts } from '@/types/messenger';

/**
 * Module-level ref for the currently active (open) conversation ID.
 * ChatArea sets this on mount/unmount so the global handler can
 * suppress notifications for the conversation the user is viewing.
 */
export let activeConversationRef: string | null = null;

export function setActiveConversation(id: string | null): void {
  activeConversationRef = id;
}

/** Notification sound singleton */
let audioInstance: HTMLAudioElement | null = null;

function playNotificationSound(): void {
  try {
    if (!audioInstance) {
      audioInstance = new Audio('/sounds/message.wav');
    }
    audioInstance.currentTime = 0;
    audioInstance.play().catch(() => {
      /* browser may block autoplay */
    });
  } catch {
    /* ignore */
  }
}

/**
 * A staff member buying in their own shop takes part twice — as the buyer and as the shop —
 * and gets the same message on both topics. Both copies invalidate their own context; only the
 * first one rings.
 */
const recentlyNotified: string[] = [];
const RECENTLY_NOTIFIED_LIMIT = 50;

function firstDelivery(messageGuid: string): boolean {
  if (recentlyNotified.includes(messageGuid)) return false;
  recentlyNotified.push(messageGuid);
  if (recentlyNotified.length > RECENTLY_NOTIFIED_LIMIT) recentlyNotified.shift();
  return true;
}

/** Shops the user is a member of, as one stable string — the subscription effect's dependency */
function membershipKey(counts: UnreadCounts): string {
  return counts.merchants
    .map((m) => m.merchantGuid)
    .sort()
    .join(',');
}

/**
 * The lists an event touches. With `recipient`: the lists of that context — for «Personal»
 * narrowed to the event's type (its tab), a shop has no tabs. Without it (a publisher older
 * than MSG-INBOX-TABS.5): every list of every context.
 */
export function listKeyForEvent(payload: MessageReceivedPayload): QueryKey {
  const { recipient, conversationType } = payload;
  if (!recipient) return conversationKeys.lists();
  if (recipient.type === 'merchant') {
    return conversationKeys.list({ context: merchantContext(recipient.guid) });
  }
  return conversationKeys.list({
    context: PERSONAL_CONTEXT,
    ...(conversationType ? { type: conversationType } : {}),
  });
}

/**
 * «Maria · Tomitos» — the person and the shop they answered for (`messages.reply_by`); the shop
 * alone when its policy hides the name; the legacy `senderName` without a structured sender.
 */
export function notificationTitleOf(payload: MessageReceivedPayload, t: TFunction): string {
  const person = payload.sender?.displayName || null;
  const shop = payload.sender?.onBehalfOf?.displayName || null;
  if (person && shop) return t('messages.reply_by', { staff: person, shop });
  return shop ?? person ?? payload.senderName;
}

export function useMessagesSync(): void {
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();
  const { data: memberships = '' } = useUnreadCounts(membershipKey);

  // The user's own topic
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) return;

    const topic = RealtimeTopics.userMessages(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // One topic per shop — events for a shop go to the shop's topic, not the person's
  // (ADR-002 D6). Re-run on a membership change: the cleanup drops the shops that went away.
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !memberships) return;

    const topics = memberships.split(',').map((guid) => RealtimeTopics.merchantMessages(guid));
    subscribe(topics);

    return () => {
      unsubscribe(topics);
    };
  }, [isConnected, isAuthenticated, memberships, subscribe, unsubscribe]);

  // Handle MESSAGE_RECEIVED events globally
  useRealtimeEventOptional<MessageReceivedPayload>(
    EventType.MESSAGE_RECEIVED,
    useCallback(
      (payload) => {
        // Skip own messages
        if (payload.senderGuid === user?.userGuid) return;

        const isActiveConversation =
          activeConversationRef === payload.conversationGuid;

        // Only invalidate for non-active conversations
        // ChatArea owns invalidation for the active conversation (messages + markAsRead)
        if (!isActiveConversation) {
          // Badges come only from the server — no client-side increment
          queryClient.invalidateQueries({ queryKey: conversationKeys.unreadCounts() });
          queryClient.invalidateQueries({ queryKey: listKeyForEvent(payload) });
          // Invalidate messages cache so opening this conversation later triggers a refetch
          // (useMessages has staleTime: Infinity — without this, cached data would be stale)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.messages(payload.conversationGuid),
          });
          // Invalidate detail cache so ChatArea's useEffect sees fresh hasUnreadMessages: true
          // and triggers markAsRead (detail has staleTime: 60s — without this, stale cache
          // with hasUnreadMessages: false would prevent the markAsRead call)
          queryClient.invalidateQueries({
            queryKey: conversationKeys.detail(payload.conversationGuid),
          });
        }

        if (!firstDelivery(payload.messageGuid)) return;

        // Show notification only when NOT on the active conversation
        if (!isActiveConversation) {
          notifications.show({
            title: notificationTitleOf(payload, t),
            message: payload.preview,
            color: 'blue',
            autoClose: 5000,
          });
        }

        // Play sound when tab hidden or not viewing that conversation
        if (document.hidden || !isActiveConversation) {
          playNotificationSound();
        }
      },
      [user?.userGuid, queryClient, t]
    ),
    [user?.userGuid, t]
  );
}
